import React from "react";
import { useNavigate } from "react-router-dom";
import NightAuditSummary from "./NightAuditSummary";

const NightAuditModal = ({ open, onClose, report, errors, onApprove }) => {
  const navigate = useNavigate();
  
  if (!open) return null;
  const safeErrors = Array.isArray(errors) ? errors : [];

  // Function to parse error message and extract relevant information
  const parseErrorMessage = (error) => {
    const { type, message } = error;
    
    // Handle PendingOrder errors
    if (type === "PendingOrder" && message.includes("Restaurant order")) {
      // Extract order ID from the message
      const orderIdMatch = message.match(/Restaurant order ([a-f0-9-]+) is not completed/);
      if (orderIdMatch) {
        const orderId = orderIdMatch[1];
        return {
          displayMessage: "Restaurant order is pending completion",
          orderId: orderId,
          type: "restaurant_order"
        };
      }
    }
    
    // Handle other types of errors
    return {
      displayMessage: message,
      type: type.toLowerCase()
    };
  };

  // Function to handle error click navigation
  const handleErrorClick = (error) => {
    const parsedError = parseErrorMessage(error);
    
    switch (parsedError.type) {
      case "restaurant_order":
        if (parsedError.orderId) {
          navigate(`/restaurant/edit-order/${parsedError.orderId}?isViewOnly=true`);
        } else {
          navigate("/restaurant/orders");
        }
        break;
      case "overduereservation":
        navigate("/reservation/booking-list");
        break;
      default:
        // For other error types, you can add more navigation logic
        console.log("Navigate to appropriate page for:", parsedError.type);
        break;
    }
    
    // Close the modal after navigation
    onClose();
  };

  // Helper to render key-value pairs as a list
  const renderList = (obj) => (
    <ul>
      {Object.entries(obj || {}).map(([key, value]) => (
        <li key={key}>
          <strong>{key}:</strong>{" "}
          {typeof value === "object" ? JSON.stringify(value) : value}
        </li>
      ))}
    </ul>
  );

  // Helper to render activity log as a table
  const renderActivityLog = (activityLog) => {
    if (
      !activityLog ||
      !Array.isArray(activityLog.staffActivity) ||
      activityLog.staffActivity.length === 0
    ) {
      return <div className="text-muted">No staff activity found.</div>;
    }
    return (
      <div className="table-responsive mb-3">
        <table className="table table-bordered table-sm">
          <thead className="table-light">
            <tr>
              <th>Staff</th>
              <th>Role</th>
              <th>Action</th>
              <th>Timestamp</th>
              <th>Remarks</th>
            </tr>
          </thead>
          <tbody>
            {activityLog.staffActivity.map((a, idx) => (
              <tr key={idx}>
                <td>{a.staffName}</td>
                <td>{a.staffRole}</td>
                <td>{a.action}</td>
                <td>
                  {a.timestamp ? new Date(a.timestamp).toLocaleString() : "-"}
                </td>
                <td>{a.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper to render actions as a list
  const renderActions = (actions) => (
    <ul>
      {(actions || []).map((action, idx) => (
        <li key={idx}>{action}</li>
      ))}
    </ul>
  );

  // Helper to render generated reports
  const renderGeneratedReports = (generatedReports) => (
    <>
      <ul>
        {(generatedReports?.generatedReports || []).map((r, idx) => (
          <li key={idx}>{r}</li>
        ))}
      </ul>
      {generatedReports?.generatedAt && (
        <div className="text-muted small">
          Generated At:{" "}
          {new Date(generatedReports.generatedAt).toLocaleString()}
        </div>
      )}
    </>
  );

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal-dialog modal-dialog-centered modal-xl">
        <div className="modal-content border-0 shadow">
          <div className="modal-header bg-primary text-white border-0">
            <h5 className="modal-title fw-bold">Night Audit Preview</h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              aria-label="Close"
              onClick={onClose}
            ></button>
          </div>
          <div className="modal-body p-4">
            {/* Show loader if report is not available */}
            {!report && (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ minHeight: 200 }}
              >
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}
            {/* Show errors if any, or 'No errors found' if empty */}
            {safeErrors.length > 0 && (
              <div className="mb-3">
                <div className="alert alert-danger">
                  <strong>Errors/Warnings:</strong>
                  <ul className="mb-0">
                    {safeErrors.map((err, idx) => {
                      const parsedError = parseErrorMessage(err);
                      return (
                        <li 
                          key={idx}
                          className="cursor-pointer"
                          style={{ 
                            cursor: 'pointer',
                            padding: '8px 12px',
                            margin: '4px 0',
                            borderRadius: '6px',
                            backgroundColor: 'rgba(220, 53, 69, 0.1)',
                            border: '1px solid rgba(220, 53, 69, 0.2)',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.2)';
                            e.target.style.transform = 'translateX(4px)';
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = 'rgba(220, 53, 69, 0.1)';
                            e.target.style.transform = 'translateX(0)';
                          }}
                          onClick={() => handleErrorClick(err)}
                        >
                          <div className="d-flex align-items-center">
                            <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>
                            <span className="fw-semibold">{parsedError.displayMessage}</span>
                            <i className="bi bi-arrow-right ms-auto text-muted"></i>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
            )}
            {report && safeErrors.length === 0 && (
              <div className="alert alert-success mb-3">
                {!report?.roomSummary
                  ? "No audit performed for this date."
                  : "No errors found."}
              </div>
            )}
            {/* Show report sections if report is available */}
            {report?.roomSummary && (
              // <>
              //   <section className="mb-3">
              //     <h6>Room & Reservation Summary</h6>
              //     {renderList(report.roomSummary)}
              //   </section>
              //   <section className="mb-3">
              //     <h6>Revenue Summary</h6>
              //     {renderList(report.revenueSummary)}
              //   </section>
              //   <section className="mb-3">
              //     <h6>Payment Summary</h6>
              //     {renderList(report.paymentSummary)}
              //   </section>
              //   <section className="mb-3">
              //     <h6>Guest Ledger</h6>
              //     {Array.isArray(report.guestLedger?.guestLedger) &&
              //     report.guestLedger.guestLedger.length > 0 ? (
              //       <pre className="bg-light p-2 rounded small mb-0">
              //         {JSON.stringify(report.guestLedger.guestLedger, null, 2)}
              //       </pre>
              //     ) : (
              //       <div className="text-muted">No guest ledger entries.</div>
              //     )}
              //   </section>
              //   <section className="mb-3">
              //     <h6>Inventory Usage</h6>
              //     {Array.isArray(report.inventoryUsage?.usageSummary) &&
              //     report.inventoryUsage.usageSummary.length > 0 ? (
              //       <pre className="bg-light p-2 rounded small mb-0">
              //         {JSON.stringify(
              //           report.inventoryUsage.usageSummary,
              //           null,
              //           2
              //         )}
              //       </pre>
              //     ) : (
              //       <div className="text-muted">
              //         No inventory usage entries.
              //       </div>
              //     )}
              //   </section>
              //   <section className="mb-3">
              //     <h6>Front Office Activity Log</h6>
              //     {renderActivityLog(report.activityLog)}
              //   </section>
              //   <section className="mb-3">
              //     <h6>Date Rollover</h6>
              //     {renderActions(report.dateRollover?.actions)}
              //   </section>
              //   <section className="mb-3">
              //     <h6>System Locking</h6>
              //     {renderActions(report.systemLocking?.actions)}
              //   </section>
              //   <section className="mb-3">
              //     <h6>Generated Reports</h6>
              //     {renderGeneratedReports(report.generatedReports)}
              //   </section>
              // </>
              <NightAuditSummary report={report}/>
            )}
          </div>
          <div className="modal-footer border-0">
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={onClose}
            >
              Close
            </button>
            {/* Uncomment if you want to allow approval in the future */}
            {/* <button type="button" className="btn btn-success" onClick={onApprove}>
              Approve & Apply
            </button> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NightAuditModal;
